import React, { useCallback, useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import McKessonPageHeader from "../McKessonPageHeader/McKessonPageHeader";
import useMckesson from "../../../Hooks/useMckesson";
import "./McItemsMaster.scss";

interface McItemRecord {
  id: string;
  itemId: string;
  description: string;
  supplierId: string;
  manufacturerId: string;
  manufacturerName: string;
  atomicUom: string;
  valid: boolean;
}

interface McItemUom {
  id: string;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
  mcKessonItemId: string;
  currency: string;
  price: number;
  quantityAvailable: number;
  per: number;
  atomic: number;
  units: string;
}

const defaultFilter = { searchText: "", active: true };

const McItemsMaster: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [items, setItems] = useState<McItemRecord[]>([]);
  const [uomData, setUomData] = useState<McItemUom[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>("");

  const {
    getMckessonItemsAPI,
    getMckessonItemsLoading,
    getMckessonUomsAPI,
    getMckessonUomsLoading,
    postMckessonItemSearchAPI,
    postMckessonItemSearchLoading,
    postMckessonItemFeedAPI,
    postMckessonItemFeedLoading,
    postMckessonItemLookupAPI,
    postMckessonItemLookupLoading,
  } = useMckesson();

  const isLoading = getMckessonItemsLoading || getMckessonUomsLoading || postMckessonItemSearchLoading || postMckessonItemFeedLoading || postMckessonItemLookupLoading;

  const loadItems = useCallback(async () => {
    try {
      const result = await getMckessonItemsAPI({
        searchText: searchVal.searchText,
        active: searchVal.active,
      });
      if (result) {
        setItems(result);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [searchVal]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleLookUp = async () => {
    try {
      const result = await postMckessonItemLookupAPI({
        itemId: searchVal.searchText,
      });
      if (result) {
        setItems(Array.isArray(result) ? result : [result]);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleItemSearch = async () => {
    try {
      const result = await postMckessonItemSearchAPI({
        query: searchVal.searchText,
        pageSize: 100,
        offset: 0,
        sort: { order: "asc", by: "itemId" },
      });
      if (result) {
        setItems(result);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleItemFeed = async () => {
    try {
      const result = await postMckessonItemFeedAPI({
        source: { type: "", text: searchVal.searchText },
        pageSize: 100,
        offset: 0,
      });
      if (result) {
        setItems(result);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleRowClick = async (item: McItemRecord) => {
    setSelectedItemId(item.id);
    try {
      const result = await getMckessonUomsAPI({
        mcKessonItemId: item.id,
        searchText: "",
        active: true,
      });
      if (result) {
        setUomData(result);
      } else {
        setUomData([]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <div className="mc-items-master-wrapper">
      {isLoading && <Loading />}

      <McKessonPageHeader
        title="McKesson Items Master"
        disRecords={items.length}
        totalRecords={items.length}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
        defaultFilter={defaultFilter}
        showClearButton={false}
        actionButtons={[
          { label: "Look Up (McKesson)", onClick: handleLookUp },
          { label: "Item Search (McKesson)", onClick: handleItemSearch },
          { label: "Item Feed (McKesson)", onClick: handleItemFeed },
        ]}
      />

      {/* Items Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover>
          <thead>
            <tr>
              <th>Item Id</th>
              <th>Description</th>
              <th>Supplier Id</th>
              <th>Manufacturer Id</th>
              <th>Manufacturer Name</th>
              <th>Atomic Uom</th>
              <th>Valid?</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr
                  key={item.id}
                  className={selectedItemId === item.id ? "selected" : ""}
                  onClick={() => handleRowClick(item)}
                >
                  <td>{item.itemId}</td>
                  <td>{item.description}</td>
                  <td>{item.supplierId}</td>
                  <td>{item.manufacturerId}</td>
                  <td>{item.manufacturerName}</td>
                  <td>{item.atomicUom}</td>
                  <td>{item.valid ? "Yes" : "No"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {/* UOM Detail Table */}
      {uomData.length > 0 && (
        <div className="mc-items-pricing-section">
          <div className="listing-table-wrapper pricing-table">
            <Table responsive="md" hover>
              <thead>
                <tr>
                  <th>Quantity Available</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Per</th>
                  <th>Atomic</th>
                  <th>Units</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {uomData.map((uom, index) => (
                  <tr key={index}>
                    <td>{uom.quantityAvailable?.toFixed(4)}</td>
                    <td>{uom.price?.toFixed(2)}</td>
                    <td>{uom.currency}</td>
                    <td>{uom.per?.toFixed(2)}</td>
                    <td>{uom.atomic?.toFixed(2)}</td>
                    <td>{uom.units}</td>
                    <td>{uom.createdBy}</td>
                    <td>{uom.createdOnUtc}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default McItemsMaster;
